import { graphql, Link, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'
import getGatsbyImage from 'utils/getGatsbyImage'
import Button from '../../UI/Button'
import useStrapiDefaults from './useStrapiDefaults'

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  > :first-child {
    position: absolute !important;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
    img {
      
    }
  }
  color: #fff;
  display: flex;
  justify-content: center;
  font-size: 1.4rem;
  min-height: 500px;
  align-items: flex-end;
  padding-bottom: 30px;
  overflow: hidden;
  img {
    user-select: none;
  }
`

const Content = styled.div`
  max-width: 1100px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
`

const CharacterFrame = styled.div`
  position: absolute;
  > * { position: absolute !important; bottom: 0; }
  bottom: 0;
  img {
    object-fit: contain !important;
  }
`

const Florence = styled(CharacterFrame)`
  left: 0;
  height: 100%;
  width: 33%;
  > * { left: 0; height: 100%; }
  > :first-child {
    bottom: 10%;
    img {
      object-position: bottom left;
    }
    transform: translate(-100%,-2%);
    animation: animate-florence-in 500ms, animate-florence infinite alternate-reverse 500ms ease-out;
    animation-delay: 1.5s, 2s;
  }
  > :nth-child(2) {
    height: 70%;
    bottom: 0;
    left: 0;
    transform: translateY(100%);
    animation: animate-girl-in 500ms forwards ease-out;
    animation-delay: 1s;
    img {
      object-position: bottom left;
    }
  }

  @media (max-width: 950px) {
    display: none;
  }
  @keyframes animate-florence-in {
    from {
      transform: translate(-100%,-2%);
    }
    to {
      transform: translate(0%,-2%);
    }
  }
  @keyframes animate-florence {
    from {
      transform: translate(0%,0%);
    }
    to {
      transform: translate(0%,-2%);
    }
  }

  @keyframes animate-girl-in {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const Sam = styled(CharacterFrame)`
  right: 0;
  height: 100%;
  width: 33%;
  > :first-child {
    transform: translate(100%,-100%);
    top: 0;
    height: 50%;
    max-height: 300px;
    z-index: 1;
    animation: animate-sam-in 1s ease-in-out forwards, animate-sam 2s infinite alternate-reverse ease-in-out;
    img {
      object-position: top right;
    }
    animation-delay: 2s, 3s;
    @media (max-width: 950px) {
      height: 100%;
      width: 100%;
      min-width: 200px;
    }
  }
  > :nth-child(2) {
    height: 70%;
    bottom: 0;
    transform: translateY(100%);
    animation: animate-boy-in 500ms forwards ease-out;
    animation-delay: 2s;
    img { object-fit: contain !important; object-position: bottom right; }

    @media (max-width: 950px) {
      display: none !important;
    }
  }
  > * { right: 0; }

  @keyframes animate-sam {
    from {
      transform: translate(15%,0%);
    }
    to {
      transform: translate(33%,-22%);
    }
  }
  @keyframes animate-sam-in {
    from {
      transform: translate(100%,-100%);
    }
    to {
      transform: translate(33%,-22%);
    }
  }
  @keyframes animate-boy-in {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const MainContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Logo = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  padding-bottom: 80px;
  padding-top: 20px;
  animation: animate-logo infinite alternate-reverse ease-in-out 7s;
  animation-delay: 3s;
  @media (max-width: 950px) {
    width: 140%;
    left: -20%;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain !important;
  }

  @keyframes animate-logo {
    from {
      transform: scale(0.9);
    }
    to {
      transform: scale(1);
    }
  }
`

const CTA = styled.div`
  margin-bottom: 10px;
`

interface IProps {
  // eslint-disable-next-line
  title: string
}

// eslint-disable-next-line
const ZooHero = (props: IProps) => {
  const { zooBg, florence, boyRiya, logoHero, sam, girlWill } = useStaticQuery(graphql`
    {
      zooBg: file(relativePath: { eq: "zoo-bg.jpg" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
        }
      }
      florence: file(relativePath: { eq: "florence.png" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, width: 453, quality: 90)
        }
      }
      boyRiya: file(relativePath: { eq: "riya-boy.png" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, height: 350, quality: 90)
        }
      }
      logoHero: file(relativePath: { eq: "logo-hero.png" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, width: 783, quality: 90)
        }
      }
      sam: file(relativePath: { eq: "sam.png" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, height: 300, quality: 90)
        }
      }
      girlWill: file(relativePath: { eq: "girl-will.png" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, height: 350, quality: 90)
        }
      }
    }
  `)


  return <>
  <Wrapper>
    <GatsbyImage image={getGatsbyImage(zooBg)} alt='Enter the Zoo' />
    <Content>
      <Florence>
        <GatsbyImage image={getGatsbyImage(florence)} alt='Florence' />
        <GatsbyImage image={getGatsbyImage(girlWill)} alt='Girl with Will' />
      </Florence>
      <MainContent>
        <Logo>
          <GatsbyImage image={getGatsbyImage(logoHero)} alt='Night Zookeeper Logo' />
        </Logo>
        <Link to='/parents'>
          <CTA>
            <Button theme='primary' size='regular'>Start Free Trial</Button>
          </CTA>
        </Link>
      </MainContent>
      <Sam>
        <GatsbyImage image={getGatsbyImage(sam)} alt='Sam' />
        <GatsbyImage image={getGatsbyImage(boyRiya)} alt='Boy with Riya' />
      </Sam>
    </Content>
  </Wrapper>
  </>
}

export const ZooHeroDefault = () => {
  const defaultProps = useStrapiDefaults()
  return <ZooHero {...defaultProps} />
}

export default ZooHero
